import { useMemo } from "react";
import {
  Coordinate,
  PointPopup,
  RenderPointType,
  RenderRouteType,
  RoutePopup,
  RouteTransportTypes,
} from "@qubictron/maplib";
import { SearchReferenceModel } from "entities/shared/shipmentCard.types";
import { filteredCardsStore } from "features/Filter";
import { openedCardStore } from "features/OpenedCard";
import { useCardHandlers } from "features/Panel";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { tagsStore } from "features/Tags";
import { airSavedCardStore, isAirSavedCardModel } from "entities/AirSavedCard";
import { airSearchCardStore, AirTransportTypeModel, useOpenedCardSearchQuery } from "entities/AirSearchCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { panelStore, PanelViewsList } from "entities/Panel";
import { searchFilterStore } from "entities/SearchFilter";
import { MapPoints } from "entities/shared/mapPointsModel";
import { RouteReturnType } from "entities/shared/mapRouteModel";
import { subscriptionStore } from "entities/Subscription";
import { useAppSelector } from "shared/lib/redux";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";

type UseAirMapContentProps = {
  isMobile: boolean;
  showPanel: () => void;
  isMapLoaded: boolean;
  isDisabled: boolean;
};

export type RouteMarkerPoint = {
  route: Coordinate;
  transport: Nullable<ValueOf<typeof AirTransportTypeModel>>;
};

type UseAirMapContentReturnType = {
  isAirRouteError: boolean;
  showAirRoute: (reference: SearchReferenceModel) => void;
  points: RenderPointType[];
  renderRoute: RenderRouteType;
  renderRoutePoints: RenderPointType[];
};

export const useAirMapContent = ({
  isMobile,
  showPanel,
  isMapLoaded,
  isDisabled,
}: UseAirMapContentProps): UseAirMapContentReturnType => {
  const currentView = useAppSelector(panelStore.selectors.currentView);
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const searchCards = useAppSelector(airSearchCardStore.selectors.searchCards);
  const savedCards = useAppSelector(airSavedCardStore.selectors.savedCards);
  const filteredCards = useAppSelector(filteredCardsStore.selectors.filteredCards);
  const cardsByTag = useAppSelector(tagsStore.selectors.cardsByTags);
  const { isCurrentViewMain, isCurrentViewOpenedCard, isCurrentViewFilteredCards, isCurrentViewCardsByTag } =
    usePanelViews();
  const { openCard } = useCardHandlers({
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
  });

  const {
    searchResponse: cardData,
    isSearchSuccess,
    isSearchError,
  } = useOpenedCardSearchQuery({
    isAuthenticated,
    setSubscription: (subscription) => ({
      type: subscriptionStore.actions.setSubscription.type,
      payload: subscription,
    }),
    setActivePaymentModal: (activePaymentModal) => ({
      type: subscriptionStore.actions.setActivePaymentModal.type,
      payload: activePaymentModal,
    }),
    setAppIntegrationRequestLimitReached: (appIntegrationRequestLimitReached) => ({
      type: appStore.actions.setAppIntegrationRequestLimitReached.type,
      payload: appIntegrationRequestLimitReached,
    }),
    setWebAppRequestLimitReached: (webAppRequestLimitReached) => ({
      type: appStore.actions.setWebAppRequestLimitReached.type,
      payload: webAppRequestLimitReached,
    }),
  });

  const mapData = useMemo(() => {
    switch (currentView) {
      case PanelViewsList.main:
        return { searchCards, savedCards };
      case PanelViewsList.cardsByTag:
        return { savedCards: cardsByTag.filter((card) => isAirSavedCardModel(card)) };
      case PanelViewsList.filteredCards:
        return { searchCards, savedCards: filteredCards.filter((card) => isAirSavedCardModel(card)) };
      default:
        return { searchCards: [], savedCards: [] };
    }
  }, [currentView, searchCards.length, savedCards.length, filteredCards.length, cardsByTag.length]);

  const points = useMemo(() => {
    if (!isMapLoaded || isDisabled) return [];

    if (!isCurrentViewMain && !isCurrentViewFilteredCards && !isCurrentViewCardsByTag) {
      return [];
    }

    const points = new MapPoints({
      searchCards:
        mapData.searchCards?.map((card) => ({
          currentPin: card.polCoordinate,
          fromDirection: card.directionFrom,
          toDirection: card.directionTo,
          reference: card.reference,
        })) ?? [],
      savedCards:
        mapData.savedCards?.map((card) => ({
          currentPin: card.currentPin,
          fromDirection: card.directionFrom,
          toDirection: card.directionTo,
          reference: card.reference,
        })) ?? [],
      type: RouteTransportTypes.AIR,
    }).points;

    return points.map((point) => ({
      ...point,
      popup: (
        <RoutePopup
          from={point.fromDirection ?? undefined}
          to={point.toDirection ?? undefined}
          shipmentType={RouteTransportTypes.AIR}
          reference={point.reference ?? ""}
          onClick={() => showRoute(point.reference ?? "")}
        />
      ),
    }));
  }, [currentView, mapData?.searchCards?.length, mapData?.savedCards?.length, isMapLoaded, shipment]);

  const route = useMemo((): RouteReturnType => {
    const route = { points: [], route: { route: [] } };
    if (!isMapLoaded || isDisabled) return route;
    if (!isCurrentViewOpenedCard && !cardData) {
      return route;
    }
    return cardData?.route?.renderRoute ?? route;
  }, [currentView, cardData?.rawData, openedCard, isMapLoaded, points.length]);

  const showRoute = (reference: SearchReferenceModel) => {
    const targetCard =
      searchCards.find((card) => card.reference === reference) ??
      savedCards.find((card) => card.reference === reference);
    if (targetCard) {
      openCard(targetCard);
    }
    if (isMobile) {
      showPanel();
    }
  };

  const renderRoutePoints = useMemo(() => {
    return route.points.map((point) => ({
      ...point,
      popup: <PointPopup label={point.label ?? ""} shipmentType={RouteTransportTypes.AIR} />,
    }));
  }, [route.points]);

  return {
    showAirRoute: showRoute,
    isAirRouteError:
      isCurrentViewOpenedCard && (isSearchError || (isSearchSuccess && (!cardData?.isSuccess || !cardData?.locations))),
    points,
    renderRoute: route.route,
    renderRoutePoints,
  };
};
